import React, { useEffect, useMemo, useState } from 'react';
import { Form, Input, Button, Card, Typography } from 'antd';
import { toast } from '../../../lib/toast';
import { Link, useSearchParams } from 'react-router-dom';
import { setPasswordApi } from '../api/auth';
import { normalizeSetPasswordUrl, readPasswordResetToken } from '../utils/passwordResetToken';
import AuthPageShell from '../../../components/AuthPageShell';

const { Text, Paragraph } = Typography;

interface FormValues {
  newPassword: string;
  confirmPassword: string;
}

const SetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [tokenFromUrl, setTokenFromUrl] = useState(() => readPasswordResetToken() ?? '');

  useEffect(() => {
    normalizeSetPasswordUrl();
    setTokenFromUrl(readPasswordResetToken() ?? searchParams.get('token') ?? '');
  }, [searchParams]);

  const token = useMemo(
    () => (tokenFromUrl || searchParams.get('token') || '').trim(),
    [tokenFromUrl, searchParams],
  );

  const hasToken = token.length > 0;

  const onFinish = async (values: FormValues) => {
    if (!hasToken) {
      toast.error('Missing or invalid link. Request a new invite or reset email.');
      return;
    }

    setLoading(true);
    try {
      const result = await setPasswordApi({
        token,
        newPassword: values.newPassword,
        confirmPassword: values.confirmPassword,
      });
      if (result.success) {
        setCompleted(true);
        toast.success('Password saved. You can sign in now.');
      } else {
        toast.error(result.error);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthPageShell>
      <Card title="Set your password" style={{ width: '100%', maxWidth: 400 }}>
        <Paragraph type="secondary" style={{ marginTop: 0 }}>
          Choose a password to activate your account or complete a password reset.
        </Paragraph>
        {!hasToken ? (
          <>
            <Text type="danger">This link is missing a token. Request a new invite or reset email.</Text>
            <div style={{ marginTop: 16 }}>
              <Link to="/forgot-password">Forgot password</Link>
            </div>
          </>
        ) : completed ? (
          <>
            <Paragraph>Your password has been saved.</Paragraph>
            <Link to="/login">Sign in</Link>
          </>
        ) : (
          <Form layout="vertical" onFinish={onFinish} autoComplete="off">
            <Form.Item
              label="New password"
              name="newPassword"
              rules={[
                { required: true, message: 'Required' },
                { min: 8, message: 'At least 8 characters' },
              ]}
            >
              <Input.Password size="large" />
            </Form.Item>
            <Form.Item
              label="Confirm password"
              name="confirmPassword"
              dependencies={['newPassword']}
              rules={[
                { required: true, message: 'Required' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Passwords do not match'));
                  },
                }),
              ]}
            >
              <Input.Password size="large" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading} block size="large">
                Save password
              </Button>
            </Form.Item>
          </Form>
        )}
      </Card>
    </AuthPageShell>
  );
};

export default SetPasswordPage;